/**
 * UsageAnalyticsView.tsx
 * 사용량 분석 대시보드 컴포넌트
 */

import React, { useState } from 'react';
import { alimtalkTypeLabel } from '../../../../shared/constants/alimtalkTypeLabels';
import { AlimtalkUsage, FailedNotificationLog } from '../../repositories/BillingUsageRepository';

interface UsageAnalyticsViewProps {
  usage: AlimtalkUsage | null;
  freeQuota: number;
  unitPrice: number;
  failedLogs: FailedNotificationLog[];
  selectedMonth: string;
  availableMonths: string[];
  onMonthChange: (month: string) => void;
  loading: boolean;
}

const typeColors: Record<string, string> = {
  'gate_entry_exit': '#3b82f6',
  'entry': '#06b6d4',
  'exit': '#ec4899',
  'EXPIRY_D7': '#8b5cf6',
  'EXPIRY_D3': '#a855f7',
  'EXPIRY_D1': '#f59e0b',
  'EXPIRY_D0': '#ef4444',
  'PAYMENT_COMPLETE': '#10b981',
  'MANUAL': '#6b7280',
  'ticket_expiry': '#f97316',
  'payment_complete': '#10b981',
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  monthSelect: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e5e7eb',
  },
  cardTitle: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '8px',
  },
  cardValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a1a1a',
  },
  cardSubValue: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '4px',
  },
  chartSection: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e5e7eb',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '20px',
  },
  barContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  barLabel: {
    width: '100px',
    fontSize: '14px',
    color: '#374151',
    flexShrink: 0,
  },
  barWrapper: {
    flex: 1,
    height: '24px',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative' as const,
  },
  bar: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  barValue: {
    width: '80px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#1a1a1a',
    textAlign: 'right' as const,
    flexShrink: 0,
  },
  successRate: {
    color: '#10b981',
  },
  failedRate: {
    color: '#ef4444',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  legendDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  noData: {
    textAlign: 'center' as const,
    padding: '40px',
    color: '#9ca3af',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '40px',
    color: '#6b7280',
  },
};

const formatMonth = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year}년 ${parseInt(month)}월`;
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${parseInt(month)}/${parseInt(day)}`;
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ko-KR').format(amount) + '원';
};

export const UsageAnalyticsView: React.FC<UsageAnalyticsViewProps> = ({
  usage,
  freeQuota,
  unitPrice,
  failedLogs,
  selectedMonth,
  availableMonths,
  onMonthChange,
  loading,
}) => {
  const INITIAL_SHOW_COUNT = 10;
  const [showAllFailed, setShowAllFailed] = useState(false);

  if (loading) {
    return <div style={styles.loading}>사용량 데이터를 불러오는 중...</div>;
  }

  if (!usage) {
    return <div style={styles.noData}>사용량 데이터가 없습니다</div>;
  }

  const successRate = usage.total_sent > 0
    ? Math.round((usage.success_count / usage.total_sent) * 100)
    : 0;

  const paidCount = Math.max(0, usage.success_count - freeQuota);
  const freeUsed = Math.min(usage.success_count, freeQuota);

  // by_type 데이터 정렬 (발송 건수 많은 순)
  const sortedTypes = Object.entries(usage.by_type || {})
    .map(([type, counts]) => ({
      type,
      label: alimtalkTypeLabel(type),
      color: typeColors[type] || '#6b7280',
      success: counts.success,
      failed: counts.failed,
      total: counts.success + counts.failed,
    }))
    .sort((a, b) => b.total - a.total);

  const maxCount = Math.max(...sortedTypes.map((t) => t.total), 1);

  // 청구 주기 기간 표시
  const periodText = usage.period_start && usage.period_end
    ? `${formatDate(usage.period_start)} ~ ${formatDate(usage.period_end)}`
    : null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            style={styles.monthSelect}
            value={selectedMonth}
            onChange={(e) => onMonthChange(e.target.value)}
          >
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {formatMonth(month)}
              </option>
            ))}
          </select>
          {periodText && (
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              ({periodText})
            </span>
          )}
        </div>
      </div>

      {/* 요약 카드 */}
      <div style={styles.cardsGrid}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>총 발송</div>
          <div style={styles.cardValue}>{usage.total_sent.toLocaleString()}건</div>
          <div style={styles.cardSubValue}>
            성공 {usage.success_count.toLocaleString()} / 실패 {usage.failed_count.toLocaleString()}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>발송 성공률</div>
          <div style={{ ...styles.cardValue, color: successRate >= 95 ? '#10b981' : successRate >= 80 ? '#f59e0b' : '#ef4444' }}>
            {successRate}%
          </div>
          <div style={styles.cardSubValue}>
            {successRate >= 95 ? '우수' : successRate >= 80 ? '양호' : '개선 필요'}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>무료 / 유료</div>
          <div style={styles.cardValue}>
            <span style={{ color: '#10b981' }}>{freeUsed}</span>
            <span style={{ fontSize: '18px', color: '#6b7280' }}> / </span>
            <span style={{ color: '#f59e0b' }}>{paidCount}</span>
          </div>
          <div style={styles.cardSubValue}>
            무료 한도: {freeQuota}건
          </div>
        </div>

        <div style={{ ...styles.card, border: '1px solid #bae6fd', backgroundColor: '#f0f9ff' }}>
          <div style={{ ...styles.cardTitle, color: '#0369a1' }}>예상 비용 (후불)</div>
          <div style={{ ...styles.cardValue, color: '#0369a1' }}>
            {formatCurrency(paidCount * unitPrice)}
          </div>
          <div style={{ ...styles.cardSubValue, color: '#0369a1' }}>
            {paidCount > 0
              ? `유료 ${paidCount}건 x ${unitPrice}원 (VAT 별도)`
              : '무료 한도 내 사용 중'}
          </div>
        </div>
      </div>

      {/* 종류별 발송 현황 */}
      <div style={styles.chartSection}>
        <div style={styles.chartTitle}>알림톡 종류별 발송 현황</div>

        {sortedTypes.length > 0 ? (
          <div style={styles.barContainer}>
            {sortedTypes.map(({ type, label, color, success, failed, total }) => (
              <div key={type} style={styles.barRow}>
                <div style={styles.barLabel}>{label}</div>
                <div style={styles.barWrapper}>
                  <div
                    style={{
                      ...styles.bar,
                      width: `${(total / maxCount) * 100}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
                <div style={styles.barValue}>
                  <span style={styles.successRate}>{success}</span>
                  {failed > 0 && (
                    <span style={styles.failedRate}> / {failed}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.noData}>발송 내역이 없습니다</div>
        )}

        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: '#10b981' }} />
            <span>성공</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: '#ef4444' }} />
            <span>실패</span>
          </div>
        </div>
      </div>

      {/* 실패 알림 상세 */}
      {failedLogs.length > 0 && (
        <div style={{
          ...styles.chartSection,
          border: '1px solid #fecaca',
          backgroundColor: '#fff',
        }}>
          <div style={{ ...styles.chartTitle, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>실패 알림 상세</span>
            <span style={{
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              padding: '2px 8px',
              borderRadius: '12px',
            }}>
              {failedLogs.length}건
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(showAllFailed ? failedLogs : failedLogs.slice(0, INITIAL_SHOW_COUNT)).map((log) => (
              <div key={log.id} style={{
                padding: '12px 16px',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>
                      {log.user_name}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      backgroundColor: '#e5e7eb',
                      color: '#6b7280',
                      padding: '1px 6px',
                      borderRadius: '4px',
                    }}>
                      {alimtalkTypeLabel(log.notification_type)}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#9ca3af', flexShrink: 0 }}>
                    {log.sent_at ? new Date(log.sent_at).toLocaleString('ko-KR', {
                      month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : ''}
                  </span>
                </div>
                {log.error_message && (
                  <div style={{
                    fontSize: '13px',
                    color: '#dc2626',
                    backgroundColor: '#fff',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #fecaca',
                    wordBreak: 'break-all',
                  }}>
                    {log.error_message}
                  </div>
                )}
                {!log.error_message && (
                  <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                    에러 메시지 없음 (이전 버전에서 발송된 알림)
                  </div>
                )}
              </div>
            ))}
          </div>

          {failedLogs.length > INITIAL_SHOW_COUNT && (
            <button
              onClick={() => setShowAllFailed(!showAllFailed)}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              {showAllFailed
                ? '접기'
                : `더보기 (+${failedLogs.length - INITIAL_SHOW_COUNT}건)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageAnalyticsView;
